import { Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import { WarningOutlined } from "@mui-symbols-material/w300"
import { type FC } from "react"

import { ProductTestDiffType } from "../types"

import { Button } from "@/components/common/Button"

interface ProductTestVersionUpdateDialogProps {
  diffType: ProductTestDiffType
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (isVersionUpdated: boolean) => void
}

const getDialogContent = (diffType: ProductTestDiffType) => {
  switch (diffType) {
    case ProductTestDiffType.ExpirationDate:
      return {
        title: "Expiration Date Updated",
        message: "Updating the COA Expiration Date will create a new lot version.",
      }
    case ProductTestDiffType.TestResults:
    default:
      return {
        title: "Test Data Updated",
        message: "Updating the COA Test Results will create a new lot version.",
      }
  }
}

export const ProductTestVersionUpdateDialog: FC<ProductTestVersionUpdateDialogProps> = ({
  diffType,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const { title, message } = getDialogContent(diffType as ProductTestDiffType)

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth data-testid='product-test-version-update-dialog'>
      <DialogContent className='p-10 text-center'>
        <WarningOutlined className='mb-4 size-[48px] text-secondary-600' />
        <Typography variant='h5' color='primary' component='h2' className='mb-6' data-testid='title'>
          {title}
        </Typography>
        <Typography variant='body1' className='text-center text-gray-700' data-testid='message'>
          {message}
        </Typography>
      </DialogContent>
      <DialogActions className='gap-6 px-10 pb-10'>
        <Button
          appearance='outlined'
          variant='primary'
          className='mr-auto'
          onClick={onClose}
          data-testid='cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='text'
          variant='primary'
          disabled={isSubmitting}
          onClick={() => onSubmit(false)}
          data-testid='use-current-lot-button'
        >
          Use Current Lot#
        </Button>
        <Button
          variant='primary'
          disabled={isSubmitting}
          onClick={() => onSubmit(true)}
          data-testid='update-lot-button'
        >
          Update Lot Version
        </Button>
      </DialogActions>
    </Dialog>
  )
}
